import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import {
  PlusIcon,
  TrashIcon,
  ClockClockwiseIcon,
  KeyIcon,
  UserIcon,
  ProhibitIcon,
  CheckCircleIcon,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const daysBetween = (a, b) => Math.round((a - b) / (1000 * 60 * 60 * 24));

export const UsersPage = () => {
  const { t, lang } = useLang();
  const [users, setUsers] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [extendTarget, setExtendTarget] = useState(null);
  const [pwTarget, setPwTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/users");
    setUsers(data);
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (u) => {
    try {
      await api.patch(`/admin/users/${u.user_id}`, { is_active: !u.is_active });
      toast.success(t("savedSettings"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/users/${deleteTarget.user_id}`);
      toast.success(t("deletedUser"));
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const nowMs = Date.now();

  return (
    <div className="space-y-8" data-testid="users-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">/admin</div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">{t("accounts")}</h1>
        </div>
        <button
          onClick={() => setOpenCreate(true)}
          data-testid="add-user-btn"
          className="inline-flex items-center gap-2 bg-white text-black px-4 py-3 font-semibold hover:bg-zinc-100 transition-colors"
        >
          <PlusIcon size={16} weight="bold" /> {t("createAccount")}
        </button>
      </div>

      <div className="border border-white/10 bg-[#121212] overflow-x-auto">
        <table className="w-full data-table text-sm">
          <thead>
            <tr className="text-zinc-500 text-xs uppercase tracking-[0.2em]">
              <th className="text-start px-4 py-3">{t("username")}</th>
              <th className="text-start px-4 py-3">{t("officeName")}</th>
              <th className="text-start px-4 py-3">{t("emailCol")}</th>
              <th className="text-start px-4 py-3">{t("role")}</th>
              <th className="text-start px-4 py-3">{t("expires")}</th>
              <th className="text-start px-4 py-3">{t("statusCol")}</th>
              <th className="text-end px-4 py-3">{t("actions")}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const exp = u.expires_at ? new Date(u.expires_at) : null;
              const daysLeft = exp ? daysBetween(exp.getTime(), nowMs) : null;
              const expired = exp && exp.getTime() < nowMs;
              return (
                <tr key={u.user_id} className="hover:bg-[#1C1C1C]" data-testid={`user-row-${u.username}`}>
                  <td className="px-4 py-3 font-mono font-semibold">{u.username}</td>
                  <td className="px-4 py-3 text-zinc-300">{u.office_name || "—"}</td>
                  <td className="px-4 py-3 text-zinc-400 text-xs font-mono" dir="ltr">{u.email || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs uppercase tracking-[0.2em] border px-2 py-1 ${u.role === "admin" ? "border-[#39FF14]/40 text-[#39FF14]" : "border-white/10 text-zinc-400"}`}>
                      {u.role === "admin" ? t("roleAdmin") : t("roleOffice")}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {exp ? (
                      <div>
                        <div className="text-zinc-300">{exp.toLocaleDateString(lang === "ar" ? "ar-EG" : "en-US")}</div>
                        <div className={`text-xs ${expired ? "text-[#FF3B30]" : daysLeft < 30 ? "text-[#FFEA00]" : "text-zinc-500"}`}>
                          {expired ? t("expired") : `${daysLeft} ${t("daysLeft")}`}
                        </div>
                      </div>
                    ) : (
                      <span className="text-zinc-500 text-xs uppercase tracking-[0.2em]">{t("noExpiry")}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {u.is_active ? (
                      <span className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-[#39FF14]">
                        <CheckCircleIcon size={12} weight="bold" /> {t("active")}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-[#FF3B30]">
                        <ProhibitIcon size={12} weight="bold" /> {t("disabled")}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 justify-end">
                      {u.role !== "admin" && (
                        <>
                          <button
                            data-testid={`extend-user-${u.username}`}
                            onClick={() => setExtendTarget(u)}
                            title={t("extendPeriod")}
                            className="p-2 border border-white/10 hover:bg-[#1C1C1C]"
                          >
                            <ClockClockwiseIcon size={14} weight="bold" />
                          </button>
                          <button
                            data-testid={`toggle-user-${u.username}`}
                            onClick={() => toggleActive(u)}
                            title={u.is_active ? t("disable") : t("enable")}
                            className="p-2 border border-white/10 hover:bg-[#1C1C1C]"
                          >
                            {u.is_active ? <ProhibitIcon size={14} weight="bold" /> : <CheckCircleIcon size={14} weight="bold" />}
                          </button>
                        </>
                      )}
                      <button
                        data-testid={`pw-user-${u.username}`}
                        onClick={() => setPwTarget(u)}
                        title={t("changePassword")}
                        className="p-2 border border-white/10 hover:bg-[#1C1C1C]"
                      >
                        <KeyIcon size={14} weight="bold" />
                      </button>
                      {u.role !== "admin" && (
                        <button
                          data-testid={`delete-user-${u.username}`}
                          onClick={() => setDeleteTarget(u)}
                          className="p-2 border border-white/10 hover:bg-[#1C1C1C] text-[#FF3B30]"
                        >
                          <TrashIcon size={14} weight="bold" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <CreateUserDialog open={openCreate} onOpenChange={setOpenCreate} onSaved={() => { setOpenCreate(false); load(); }} />
      <ExtendDialog target={extendTarget} onClose={() => setExtendTarget(null)} onSaved={() => { setExtendTarget(null); load(); }} />
      <PasswordDialog target={pwTarget} onClose={() => setPwTarget(null)} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#121212] border border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteUser")}</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {t("confirmDeleteUser")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border border-white/10 text-white hover:bg-[#1C1C1C]">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="delete-user-confirm" className="bg-[#FF3B30] text-white hover:bg-[#E12E24]">
              {t("deleteUser")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const CreateUserDialog = ({ open, onOpenChange, onSaved }) => {
  const { t } = useLang();
  const [form, setForm] = useState({
    username: "", password: "", office_name: "", email: "",
    period_value: 1, period_unit: "months",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ username: "", password: "", office_name: "", email: "", period_value: 1, period_unit: "months" });
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/users", {
        ...form,
        email: form.email || null,
        period_value: Number(form.period_value),
      });
      toast.success(t("accountCreated"));
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121212] border border-white/10 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("createAccount")}</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs uppercase tracking-[0.2em]">
            {t("createAccountDesc")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">{t("username")} *</Label>
              <Input data-testid="new-user-username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required dir="ltr" className="bg-[#0A0A0A] border-white/10" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">{t("password")} *</Label>
              <Input data-testid="new-user-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required dir="ltr" className="bg-[#0A0A0A] border-white/10" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">{t("officeName")}</Label>
            <Input data-testid="new-user-office" value={form.office_name} onChange={(e) => setForm({ ...form, office_name: e.target.value })} className="bg-[#0A0A0A] border-white/10" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">{t("emailLabel")}</Label>
            <Input data-testid="new-user-email" type="email" dir="ltr" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#0A0A0A] border-white/10" placeholder="office@example.com" />
            <p className="text-[10px] text-zinc-500">{t("emailHint")}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">{t("periodValue")}</Label>
              <Input data-testid="new-user-period-value" type="number" min="1" value={form.period_value} onChange={(e) => setForm({ ...form, period_value: e.target.value })} className="bg-[#0A0A0A] border-white/10" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">{t("periodUnit")}</Label>
              <Select value={form.period_unit} onValueChange={(v) => setForm({ ...form, period_unit: v })}>
                <SelectTrigger data-testid="new-user-period-unit" className="bg-[#0A0A0A] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border border-white/10 text-white">
                  <SelectItem value="months">{t("months")}</SelectItem>
                  <SelectItem value="years">{t("years")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <button type="button" onClick={() => onOpenChange(false)} className="border border-white/10 bg-transparent hover:bg-[#1C1C1C] px-4 py-2 text-sm">{t("cancel")}</button>
            <button type="submit" disabled={saving} data-testid="new-user-save" className="bg-white text-black px-4 py-2 text-sm font-semibold hover:bg-zinc-100 disabled:opacity-60">
              {t("createAccount")}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ExtendDialog = ({ target, onClose, onSaved }) => {
  const { t } = useLang();
  const [value, setValue] = useState(1);
  const [unit, setUnit] = useState("months");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (target) { setValue(1); setUnit("months"); } }, [target]);

  const submit = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/users/${target.user_id}`, {
        extend_value: Number(value),
        extend_unit: unit,
      });
      toast.success(t("periodExtended"));
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="bg-[#121212] border border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle>{t("extendPeriod")}</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs uppercase tracking-[0.2em]">
            {target?.username}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">{t("periodValue")}</Label>
              <Input data-testid="extend-value" type="number" min="1" value={value} onChange={(e) => setValue(e.target.value)} className="bg-[#0A0A0A] border-white/10" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">{t("periodUnit")}</Label>
              <Select value={unit} onValueChange={setUnit}>
                <SelectTrigger data-testid="extend-unit" className="bg-[#0A0A0A] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border border-white/10 text-white">
                  <SelectItem value="months">{t("months")}</SelectItem>
                  <SelectItem value="years">{t("years")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <button type="button" onClick={onClose} className="border border-white/10 bg-transparent hover:bg-[#1C1C1C] px-4 py-2 text-sm">{t("cancel")}</button>
            <button onClick={submit} disabled={saving} data-testid="extend-save" className="bg-white text-black px-4 py-2 text-sm font-semibold hover:bg-zinc-100 disabled:opacity-60">
              {t("extendPeriod")}
            </button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const PasswordDialog = ({ target, onClose }) => {
  const { t } = useLang();
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (target) setPw(""); }, [target]);

  const submit = async () => {
    if (!pw) return;
    setSaving(true);
    try {
      await api.patch(`/admin/users/${target.user_id}`, { password: pw });
      toast.success(t("passwordUpdated"));
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="bg-[#121212] border border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle>{t("changePassword")}</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs uppercase tracking-[0.2em]">
            {target?.username}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">{t("newPassword")}</Label>
            <Input data-testid="pw-new" value={pw} onChange={(e) => setPw(e.target.value)} dir="ltr" className="bg-[#0A0A0A] border-white/10 font-mono" />
          </div>
          <DialogFooter className="gap-2">
            <button type="button" onClick={onClose} className="border border-white/10 bg-transparent hover:bg-[#1C1C1C] px-4 py-2 text-sm">{t("cancel")}</button>
            <button onClick={submit} disabled={saving || !pw} data-testid="pw-save" className="bg-white text-black px-4 py-2 text-sm font-semibold hover:bg-zinc-100 disabled:opacity-60">
              {t("save")}
            </button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};
